import { useState, useEffect } from "react";
import her from "./assets/her.jpeg";

export default function PortfolioWebsite() {
  const [activeSection, setActiveSection] = useState("home");
  const [scrollPosition, setScrollPosition] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    let timeoutId = null;
    const handleScroll = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        const position = window.scrollY;
        setScrollPosition(position);
        
        const sections = document.querySelectorAll("section");
        sections.forEach(section => {
          const sectionTop = section.offsetTop - 100;
          const sectionHeight = section.offsetHeight;
          if (position >= sectionTop && position < sectionTop + sectionHeight) {
            setActiveSection(section.id);
          }
        });
      }, 100); // Adjust the debounce delay as needed
    };
  
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  const navItems = [
    { id: "home", label: "Home" },
    { id: "about", label: "About" },
    { id: "skills", label: "Skills" },
    { id: "projects", label: "Projects" },
    { id: "experience", label: "Experience" },
    { id: "education", label: "Education" },
    { id: "contact", label: "Contact" }
  ];

  const skills = [
    { name: "Python", icon: "🐍" },
    { name: "HTML", icon: "🌐" },
    { name: "CSS", icon: "🎨" },
    { name: "JavaScript", icon: "⚙️" },
    { name: "React", icon: "⚛️" },
    { name: "Git", icon: "📂" }
  ];

  const projects = [
    {
      title: "Ersa Studios",
      date: "December 2024",
      description: [
        "Developed a React-based website for Ersa Studios, a consulting group, using React JS, Vite, and modern web technologies.",
        "Designed a custom UI/UX in Figma and implemented a responsive layout to ensure seamless functionality across different viewports."
      ],
      link: "https://ersa.dev",
      image: '/pics/image.png'
    },
    {
      title: "Toodles",
      date: "January 2025",
      description: [
        "Developed a To-Do app for Android using Java, designed to mimic the clean aesthetic of a stock Windows Phone app.",
        "Created a UI/UX prototype in Figma, incorporating feedback from multiple stakeholders to enhance usability and design consistency."
      ],
      link: "#",
      image: '/pics/0x0.webp'
    },
    {
      title: "WeatherView",
      date: "February 2025",
      description: [
        "Developed a dynamic weather application using React and wttr.in, featuring real-time weather updates with smooth animated transitions.",
        "Implemented location-based search, dynamic background changes, and weather-specific animations for an immersive user experience.",
        "Optimized API calls and UI performance for a responsive, fluid interface."
      ],
      link: "#",
      image: '/pics/weather.jpg'
    }
  ];

  const experiences = [
    {
      role: "Senior Core Committee Member",
      organization: "CodeChef-VIT Student Chapter",
      period: "March 2021 - March 2023",
      responsibilities: [
        "Led the management and editorial domains, guiding juniors in club-related tasks.",
        "Successfully conducted Gravitas 2022 and organized the 'Creat-a-thon,' a product design hackathon.",
        "Oversaw event organization, content creation, and team management."
      ]
    },
    {
      role: "Secretary",
      organization: "VIT Animation Club",
      period: "January 2022 - May 2023",
      responsibilities: [
        "Acted as Point of Contact between externals and the club.",
        "Responsible for the entire management team and editorial team.",
        "Ideated new events for the club to conduct.",
        "Maintained the club website and all social media accounts.",
        "Managed the entire club operation process, including approvals and other logistical issues."
      ]
    }
  ];

  const education = [
    {
      degree: "B.Tech in Information Technology",
      institution: "VIT, Vellore",
      period: "2020 - 2024",
      score: "CGPA: 7.35"
    },
    {
      degree: "XIIth Grade (Senior Secondary), CBSE",
      period: "2019",
      score: "Percentage: 93.6%"
    },
    {
      degree: "Xth Grade (Secondary), CBSE",
      period: "2017",
      score: "CGPA: 10"
    }
  ];

  const certifications = [
    {
      name: "Microsoft Certified: Security, Compliance, and Identity Fundamentals",
      issuer: "Microsoft",
      date: "Issued June 2023",
      icon: "📜"
    },
    {
      name: "Web Development Bootcamp",
      issuer: "Udemy",
      date: "Issued June 2023",
      icon: "🌐"
    },
    {
      name: "Complete Java Development Bootcamp",
      issuer: "Udemy",
      date: "Issued June 2023",
      icon: "☕"
    }
  ];

  return (
    <div className="text-gray-800 bg-gray-50 overflow-x-hidden">
      {/* Flowing Blobs Background */}
      <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-96 h-96 bg-red-400 rounded-full opacity-10 blur-3xl transform translate-x-1/4 -translate-y-1/2"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-400 rounded-full opacity-10 blur-3xl transform -translate-x-1/4 translate-y-1/2"></div>
        <div className="absolute top-1/2 left-1/3 w-80 h-80 bg-teal-300 rounded-full opacity-10 blur-3xl"></div>
      </div>

      {/* Navigation */}
      <header className={`sticky top-0 z-50 bg-white bg-opacity-90 shadow transition-all duration-300 ${scrollPosition > 50 ? "py-3" : "py-5"}`}>
        <nav className="max-w-6xl mx-auto px-4 flex justify-between items-center">
          <div className="text-xl font-bold text-red-500">SB</div>
          <div className="hidden md:flex space-x-6">
            {navItems.map(item => (
              <a 
                key={item.id} 
                href={`#${item.id}`} 
                className={`relative px-2 py-1 transition-colors hover:text-red-500 ${activeSection === item.id ? "text-red-500" : ""}`}
              >
                {item.label}
                {activeSection === item.id && (
                  <span className="absolute bottom-0 left-0 w-full h-0.5 bg-red-500 transform origin-bottom"></span>
                )}
              </a>
            ))}
          </div>
          <div className="md:hidden">
            <button className="text-gray-800" onClick={() => setMenuOpen(!menuOpen)}>
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16"></path>
              </svg>
            </button>
            {menuOpen && (
              <div className="absolute top-full left-0 w-full bg-white shadow-md md:hidden">
                {navItems.map(item => (
                  <a 
                    key={item.id}
                    href={`#${item.id}`}
                    className="block px-4 py-2 text-gray-700 hover:bg-red-100"
                    onClick={() => setMenuOpen(false)}
                  >
                    {item.label}
                  </a>
                ))}
              </div>
            )}
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <section id="home" className="min-h-screen flex items-center py-20">
        <div className="max-w-6xl mx-auto px-4 flex flex-col md:flex-row items-center gap-12">
          <div className="text-center md:text-left md:w-1/2">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">Shreya Basu</h1>
            <h2 className="text-2xl md:text-3xl text-red-500 mb-6">Information Technology Graduate</h2>
            <p className="text-lg mb-8 max-w-lg mx-auto md:mx-0">
              Dynamic IT professional with a strong interest in cloud computing, DBMS, and web development. 
              Actively seeking to contribute to organizational growth through technical expertise.
            </p>
            <a href="#contact" className="inline-block px-8 py-3 bg-red-500 text-white rounded-full border-2 border-red-500 hover:bg-transparent hover:text-red-500 transition-colors">
              Get In Touch
            </a>
          </div>
          <div className="md:w-1/2 flex justify-center">
            <img src={her} alt="Shreya Basu" className="rounded-2xl shadow-lg max-w-full h-auto" />
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-16 relative">
            About Me
            <span className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-20 h-1 bg-red-500 -mb-4"></span>
          </h2>
          <div className="max-w-3xl mx-auto">
            <h3 className="text-2xl text-blue-600 mb-6">Who I Am</h3>
            <div className="space-y-6 text-lg">
              <p>
                I am a passionate Information Technology graduate with a strong interest in cloud computing, 
                database management systems, and web development. My academic background combined with 
                hands-on project experience has equipped me with the technical skills needed to excel in the IT industry.
              </p>
              <p>
                I am trilingual, fluent in English, Hindi, and Bengali, which enhances my ability to work in diverse teams. 
                I'm known for my adaptability, time management, and problem-solving skills, allowing me to effectively 
                tackle complex technical challenges.
              </p>
              <p>
                Currently, I am actively seeking an entry-level position in the IT sector to gain hands-on experience 
                and enhance my technical skills while contributing to organizational growth.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Skills Section */}
      <section id="skills" className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-16 relative">
            Technical Skills
            <span className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-20 h-1 bg-red-500 -mb-4"></span>
          </h2>
          <div className="flex flex-wrap justify-center gap-6">
            {skills.map((skill, index) => (
              <div 
                key={index} 
                className="bg-white p-6 rounded-lg shadow-md text-center w-32 transition-transform hover:-translate-y-2"
              >
                <div className="text-4xl mb-4 text-red-500">{skill.icon}</div>
                <h3 className="font-medium">{skill.name}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section id="projects" className="py-20">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-16 relative">
            Projects
            <span className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-20 h-1 bg-red-500 -mb-4"></span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {projects.map((project, index) => (
              <div 
                key={index} 
                className="bg-white rounded-xl overflow-hidden shadow-md transition-transform hover:-translate-y-2"
              >
                <img src={project.image} alt={project.title} className="w-full h-48 object-cover" />
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-2">{project.title}</h3>
                  <p className="text-red-500 italic text-sm mb-4">{project.date}</p>
                  <div className="">
                    {project.description.map((desc, i) => (
                      <p key={i} className="text-sm">{desc}</p>
                    ))}
                  </div>
                  {/* <a 
                    href={project.link} 
                    className="inline-block px-4 py-2 bg-blue-600 text-white rounded-full text-sm hover:bg-red-500 transition-colors"
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    View Project
                  </a> */}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Experience Section */}
      <section id="experience" className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-16 relative">
            Experience
            <span className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-20 h-1 bg-red-500 -mb-4"></span>
          </h2>
          <div className="relative max-w-3xl mx-auto pt-8">
            {/* Timeline Line */}
            <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 h-full w-0.5 bg-teal-200"></div>
            
            {experiences.map((exp, index) => (
              <div key={index} className="mb-16 relative">
                {/* Timeline Dot */}
                <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 w-4 h-4 rounded-full bg-red-500"></div>
                
                <div className={`md:w-5/12 ${index % 2 === 0 ? 'md:ml-auto md:pl-8' : 'md:mr-auto md:pr-8'}`}>
                  <div className="bg-white p-6 rounded-lg shadow-md">
                    <p className="text-red-500 font-medium mb-2">{exp.period}</p>
                    <h3 className="text-xl font-bold mb-1">{exp.role}</h3>
                    <p className="text-blue-600 mb-4">{exp.organization}</p>
                    <ul className="list-disc list-inside space-y-2 text-sm">
                      {exp.responsibilities.map((resp, i) => (
                        <li key={i}>{resp}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Education Section */}
      <section id="education" className="py-20">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-16 relative">
            Education
            <span className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-20 h-1 bg-red-500 -mb-4"></span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {education.map((edu, index) => (
              <div 
                key={index} 
                className="bg-white p-8 rounded-lg shadow-md transition-transform hover:-translate-y-2"
              >
                <p className="text-red-500 font-medium mb-2">{edu.period}</p>
                <h3 className="text-xl font-bold mb-2">{edu.degree}</h3>
                {edu.institution && <p className="text-blue-600 mb-4">{edu.institution}</p>}
                <p>{edu.score}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications Section */}
      <section id="certifications" className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-16 relative">
            Certifications
            <span className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-20 h-1 bg-red-500 -mb-4"></span>
          </h2>
          <div className="flex flex-wrap justify-center gap-8">
            {certifications.map((cert, index) => (
              <div 
                key={index} 
                className="bg-white p-6 rounded-lg shadow-md text-center w-64 transition-transform hover:scale-105"
              >
                <div className="text-4xl mb-4 text-red-500">{cert.icon}</div>
                <h3 className="text-lg font-medium mb-2">{cert.name}</h3>
                <p className="text-blue-600 text-sm mb-1">{cert.issuer}</p>
                <p className="text-sm">{cert.date}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-16 relative">
            Contact Me
            <span className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-20 h-1 bg-red-500 -mb-4"></span>
          </h2>
          <div className="flex flex-col md:flex-row gap-12">
            <div className="md:w-1/2">
              <h3 className="text-2xl text-blue-600 mb-6">Get In Touch</h3>
              <p className="mb-8">Feel free to reach out to me for job opportunities, collaborations, or just to say hello!</p>
              
              <div className="space-y-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center text-lg mr-4">✉️</div>
                  <div>
                    <h4 className="font-bold">Email</h4>
                    <p>shreyaaa1509@gmail.com</p>
                  </div>
                </div>
                
                
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center text-lg mr-4">🌐</div>
                  <div>
                    <h4 className="font-bold">Languages</h4>
                    <p>English, Hindi, Bengali</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="md:w-1/2">
              <form className="bg-white p-8 rounded-lg shadow-md">
                <div className="mb-4">
                  <input 
                    type="text" 
                    className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500" 
                    placeholder="Your Name" 
                    required 
                  />
                </div>
                <div className="mb-4">
                  <input 
                    type="email" 
                    className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500" 
                    placeholder="Your Email" 
                    required 
                  />
                </div>
                <div className="mb-4">
                  <input 
                    type="text" 
                    className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500" 
                    placeholder="Subject" 
                  />
                </div>
                <div className="mb-6">
                  <textarea 
                    className="w-full p-3 border border-gray-300 rounded-md h-32 focus:outline-none focus:ring-2 focus:ring-red-500" 
                    placeholder="Your Message"
                    required
                  ></textarea>
                </div>
                <button 
                  type="submit" 
                  className="w-full p-3 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                >
                  Send Message
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <div className="flex justify-center gap-6 mb-6">
            <a href="/" className="w-10 h-10 bg-white bg-opacity-10 rounded-full flex items-center justify-center hover:bg-red-500 transition-colors">
              <span>🔗</span>
            </a>
            <a href="/" className="w-10 h-10 bg-white bg-opacity-10 rounded-full flex items-center justify-center hover:bg-red-500 transition-colors">
              <span>💼</span>
            </a>
            <a href="/" className="w-10 h-10 bg-white bg-opacity-10 rounded-full flex items-center justify-center hover:bg-red-500 transition-colors">
              <span>📧</span>
            </a>
          </div>
          <p className="text-gray-400">© 2025 Shreya Basu. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}